import React, { useId, useState } from "react";

import { faChevronDown } from "@fortawesome/pro-solid-svg-icons";
import { AnimatePresence, m } from "framer-motion";
import { twJoin } from "tailwind-merge";

import { Icon } from "src/design-system/Icon";

type ControlledProps = {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  defaultOpen?: never;
};

type UncontrolledProps = {
  defaultOpen?: boolean;
  onOpenChange?: never;
  isOpen?: never;
};

type CollapsibleWidgetProps = (ControlledProps | UncontrolledProps) & {
  title: React.ReactNode;
  rightSlot?: React.ReactNode;
  children: React.ReactNode;
  dataLoc?: string;
};

// Intentionally not built on Headless UI's Disclosure: the component is simple
// enough to wire ARIA manually, and Disclosure has no controlled `open` API —
// supporting controlled state on top of it would require fighting its internal
// state, which is more complex than the plain button + panel below.
export const CollapsibleWidget: React.FC<CollapsibleWidgetProps> = ({
  title,
  rightSlot,
  children,
  dataLoc = "collapsible-widget",
  ...props
}) => {
  const buttonId = useId();
  const panelId = useId();
  const isControlled = props.isOpen !== undefined;
  const [internalOpen, setInternalOpen] = useState(
    () => (!isControlled && props.defaultOpen) ?? false,
  );

  const isOpen = isControlled ? props.isOpen : internalOpen;

  const handleToggle = () => {
    if (isControlled) {
      props.onOpenChange(!props.isOpen);
    } else {
      setInternalOpen((prev) => !prev);
    }
  };

  return (
    <div
      className="overflow-hidden rounded-lg border border-gray-200 bg-white"
      data-loc={dataLoc}
    >
      <div className="flex h-13 items-stretch gap-x-2 pl-4">
        <button
          aria-controls={panelId}
          aria-expanded={isOpen}
          className="flex flex-1 items-center gap-x-2 text-left font-inter-semibold-13px text-gray-800 focus:outline-none focus-visible:outline-none"
          data-loc={`${dataLoc}-trigger`}
          id={buttonId}
          type="button"
          onClick={handleToggle}
        >
          <div
            className={twJoin(
              "flex h-5 w-5 shrink-0 items-center justify-center transition-transform duration-200",
              isOpen ? "rotate-0" : "-rotate-90",
            )}
          >
            <Icon icon={faChevronDown} size="xs" />
          </div>
          <div className="min-w-0 flex-1">{title}</div>
        </button>
        {rightSlot && (
          <div className="flex shrink-0 items-center">{rightSlot}</div>
        )}
      </div>
      <AnimatePresence initial={false}>
        {isOpen && (
          <m.div
            key="panel"
            animate={{
              height: "auto",
              opacity: 1,
              overflow: "hidden",
              transitionEnd: { overflow: "visible" },
            }}
            aria-labelledby={buttonId}
            exit={{ height: 0, opacity: 0, overflow: "hidden" }}
            id={panelId}
            initial={{ height: 0, opacity: 0, overflow: "hidden" }}
            role="region"
            transition={{ duration: 0.2, ease: "easeOut" }}
          >
            <div className="border-gray-200 border-t">{children}</div>
          </m.div>
        )}
      </AnimatePresence>
    </div>
  );
};
